import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, InfoMessage } from '@alf/uikit';
import { useReferenceState } from '@namespace/common';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder,
  useFormMethods
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import { getAirCashPhones } from '../../../../api/balance';
import { PAYMENT_SYSTEMS } from '../../../../constants/paymentSystems';
import {
  PAYMENT_SYSTEMS_COMPONENTS_MAP,
  paymentSystemsComponentsMap
} from '../constants';

import styles from '../index.module.css';

const AirCash = ({ wallet, onWithdraw, defaultCountryCode = '' }) => {
  useReferenceState();
  const { t } = useI18n();
  const { currency, deposit, payMethods: payments = [] } = wallet;
  const [phoneListOptions, setPhoneListOptions] = useState([]);

  const defaultValues = {
    phoneList: '',
    phone: { code: '', number: '' },
    disabledPhone: { code: '', number: '' },
    amount: '',
    password: ''
  };
  const methods = useFormMethods({ defaultValues });
  const { watch, setValue, setError } = methods;
  const isNewPhone = watch('isNewPhone');

  const { minWithdrawNum, maxWithdrawNum } = useMemo(
    () =>
      payments.find(
        (payment) => payment.serviceId === PAYMENT_SYSTEMS.AIR_CASH
      ) || {},
    [payments]
  );

  useEffect(() => {
    (async () => {
      const phoneListResponse = await getAirCashPhones();
      const newPhoneListOptions = phoneListResponse
        ? Object.values(phoneListResponse)
            .filter((phoneData) => Boolean(phoneData?.display_withdrawal))
            .map((phoneData) => ({
              name: phoneData?.phone_number,
              value: phoneData?.phone_number
            }))
        : [];
      if (!newPhoneListOptions.length) {
        setValue('isNewPhone', true);
      }
      setPhoneListOptions(newPhoneListOptions);
    })();
  }, [setError, setValue, t]);

  const dynamicValues = {
    phoneList: phoneListOptions[0]?.value ?? ''
  };

  const dynamicPhoneValues = useMemo(
    () => ({
      phone: {
        isoCode: defaultCountryCode,
        number: ''
      }
    }),
    [defaultCountryCode]
  );

  const onSubmit = useCallback(
    async ({ password, amount, phone, phoneList }) => {
      const newPhone = `${phone?.code}${phone?.number}`;
      const successResult = await onWithdraw({
        serviceId: PAYMENT_SYSTEMS.AIR_CASH,
        phoneNumber: isNewPhone ? newPhone : phoneList,
        password,
        amount
      });
      if (successResult) {
        setValue('phoneList', phoneListOptions[0]?.value ?? '');
        setValue('amount', '');
        setValue('password', '');
        setValue('isNewPhone', false);
      }
    },
    [phoneListOptions, isNewPhone, setValue]
  );

  const componentsList = [
    {
      type: FORM_COMPONENTS_TYPES.SELECT,
      name: 'phoneList',
      label: 'balance.deposit.airCash.phoneNumbers',
      placeholder: 'balance.deposit.airCash.phoneNumbers',
      classNames: {
        containerClassName: styles.fieldWrap
      },
      options: phoneListOptions,
      disabled: isNewPhone
    },
    {
      type: FORM_COMPONENTS_TYPES.CHECKBOX_FIELD,
      name: 'isNewPhone',
      label: 'balance.deposit.airCash.newPhone',
      classNames: {
        wrapperClassName: `${styles.inputWrapper} ${styles.checkboxWrapper}`
      },
      disabled: !phoneListOptions?.length
    },
    {
      type: FORM_COMPONENTS_TYPES.REG_PHONE,
      name: isNewPhone ? 'phone' : 'disabledPhone',
      placeholder: 'balance.deposit.airCash.phoneNumber',
      ...(isNewPhone && {
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          FORM_VALIDATION_TYPES.PHONE_ONLY_NUMBER
        ]
      }),
      classNames: {
        wrapperClassName: styles.fieldWrap
      },
      size: 's',
      disabled: !isNewPhone,
      formProps: { dynamicValues: dynamicPhoneValues }
    },
    {
      type: FORM_COMPONENTS_TYPES.AMOUNT_INPUT_FIELD,
      name: 'amount',
      currency,
      label: 'balance.withdraw.form.amount',
      placeholder: 'balance.withdraw.airCash.amountPlaceholder',
      min: minWithdrawNum,
      inputType: 'number',
      validationTypes: [
        FORM_VALIDATION_TYPES.REQUIRED,
        {
          type: FORM_VALIDATION_TYPES.MIN_VALIDATOR,
          parameters: [
            minWithdrawNum,
            'balance.withdraw.form.minAmount',
            [currency]
          ]
        },
        {
          type: FORM_VALIDATION_TYPES.MAX_VALIDATOR,
          parameters: [
            maxWithdrawNum ? Math.min(deposit, maxWithdrawNum) : deposit,
            'balance.withdraw.form.maxAmount',
            [currency]
          ]
        }
      ],
      hint: minWithdrawNum
        ? t('balance.withdraw.form.minAmount', [minWithdrawNum, currency])
        : '',
      classNames: {
        containerClassName: styles.fieldWrap,
        controlClassName: styles.controlWrap
      },
      isOnChangeValidation: true
    },
    {
      type: FORM_COMPONENTS_TYPES.PASSWORD_FIELD,
      name: 'password',
      classNames: {
        wrapperClassName: styles.fieldWrap
      },
      rulesList: [],
      autoComplete: 'password',
      label: 'balance.withdraw.form.password',
      placeholder: 'balance.withdraw.airCash.passwordPlaceholder',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      isOnChangeValidation: true
    },
    {
      type: PAYMENT_SYSTEMS_COMPONENTS_MAP.PAYMENT_ICONS,
      name: 'paymentIcons',
      classNames: {
        wrapperClassName: styles.fieldWrap
      }
    },
    {
      type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
      value: 'balance.withdraw.form.submit',
      classNames: {
        className: styles.withdrawButton
      }
    }
  ];

  return (
    <Box direction="column" className={styles.container}>
      <InfoMessage icon="info" className={styles.info}>
        {t('balance.withdraw.airCash.info')}
      </InfoMessage>
      <FormBuilder
        wrapperClassName={styles.form}
        components={componentsList}
        componentsMap={paymentSystemsComponentsMap}
        onSubmit={onSubmit}
        formConfig={{ mode: 'onBlur', defaultValues }}
        isNoValidate={true}
        formProps={{
          dynamicValues,
          isShowPhoneMask: true,
          defaultCountryCode
        }}
        formMethods={methods}
      />
    </Box>
  );
};

export default AirCash;
